import { PdfDocumentBuilder, type FieldItem, type EmpresaInfo, type DocumentConfig } from '@/lib/pdfDocumentEngine';
import { loadEmpresaYConfigParaPdf } from '@/lib/pdfBranding';
import { sanitizarTextoContrato, type ClausulaContrato } from './plantillasPorDefecto';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export interface ContratoPdfArgs {
  empresaId: string;
  cliente: {
    nombre: string;
    nombre_fiscal?: string | null;
    nif_cif?: string | null;
    dni_nif?: string | null;
    direccion_fiscal?: string | null;
    direccion?: string | null;
    codigo_postal?: string | null;
    poblacion?: string | null;
    email?: string | null;
    telefono?: string | null;
  };
  contrato: {
    numero_formateado: string;
    tipo: 'seguridad' | 'pci';
    fecha_inicio: string;
    fecha_fin: string;
    duracion_meses: number;
    renovacion_automatica: boolean;
    periodicidad_pago: 'anual' | 'semestral' | 'trimestral' | 'mensual';
    precio_total: number;
    precio_periodo: number;
    iva_incluido: boolean;
    objeto: string;
    clausulas: ClausulaContrato[];
    texto_legal?: string;
    observaciones?: string | null;
    firma_cliente?: string | null;
    firma_empresa?: string | null;
    nombre_firmante_cliente?: string | null;
  };
  sistemas: Array<{
    tipo: string;
    tipoLabel: string;
    sedeNombre?: string;
    sedeDireccion?: string | null;
  }>;
  esBorrador?: boolean;
  /** Branding precargado (uso público sin sesión) */
  empresaOverride?: EmpresaInfo | null;
  docConfigOverride?: Partial<DocumentConfig>;
}


const PERIODICIDAD_LABEL: Record<string, string> = {
  anual: 'Anual',
  semestral: 'Semestral',
  trimestral: 'Trimestral',
  mensual: 'Mensual',
};

function eur(n: number): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(n || 0);
}

function fmtDate(s: string): string {
  try {
    return format(new Date(s), "dd 'de' MMMM 'de' yyyy", { locale: es });
  } catch {
    return s;
  }
}

export async function generarContratoPdf(args: ContratoPdfArgs): Promise<Blob> {
  let empresa: EmpresaInfo | null;
  let docConfig: Partial<DocumentConfig> | undefined;
  if (args.empresaOverride !== undefined) {
    empresa = args.empresaOverride;
    docConfig = args.docConfigOverride;
  } else {
    const loaded = await loadEmpresaYConfigParaPdf(args.empresaId);
    empresa = loaded.empresa;
    docConfig = loaded.docConfig;
  }
  const builder = await PdfDocumentBuilder.create(empresa, docConfig);

  const tituloTipo = args.contrato.tipo === 'seguridad'
    ? 'Contrato de mantenimiento — Seguridad'
    : 'Contrato de mantenimiento — PCI';

  builder.addBrandedHeader({
    titulo: tituloTipo,
    numero: args.contrato.numero_formateado,
    fechaEmision: `Emitido: ${new Date().toLocaleDateString('es-ES')}`,
  });

  // Aviso de borrador
  if (args.esBorrador) {
    builder.addSection('DOCUMENTO EN BORRADOR — NO VIGENTE');
    builder.addQuoteBlock(
      'Este documento es un BORRADOR de contrato y NO tiene efectos jurídicos ni operativos. ' +
      'Los sistemas relacionados NO generan cobertura de mantenimiento ni planificación automática de revisiones ' +
      'hasta que el contrato sea firmado por ambas partes y pase a estado "Vigente".'
    );
  }


  // Cliente + condiciones generales
  const dirCliente = [
    args.cliente.direccion_fiscal || args.cliente.direccion,
    [args.cliente.codigo_postal, args.cliente.poblacion].filter(Boolean).join(' '),
  ].filter(Boolean).join(' · ');

  const clienteFields: FieldItem[] = [
    { label: 'Razón social', value: args.cliente.nombre_fiscal || args.cliente.nombre },
    { label: 'NIF/CIF', value: args.cliente.nif_cif || args.cliente.dni_nif || '—' },
  ];
  if (dirCliente) clienteFields.push({ label: 'Domicilio', value: dirCliente });
  if (args.cliente.email) clienteFields.push({ label: 'Email', value: args.cliente.email });
  if (args.cliente.telefono) clienteFields.push({ label: 'Teléfono', value: args.cliente.telefono });

  const condicionesFields: FieldItem[] = [
    { label: 'Inicio', value: fmtDate(args.contrato.fecha_inicio) },
    { label: 'Fin', value: fmtDate(args.contrato.fecha_fin) },
    { label: 'Duración', value: `${args.contrato.duracion_meses} meses` },
    { label: 'Renovación', value: args.contrato.renovacion_automatica ? 'Tácita' : 'No automática' },
  ];

  builder.addInfoCardRow(
    { title: 'Cliente', fields: clienteFields },
    { title: 'Vigencia', fields: condicionesFields },
  );

  // Objeto
  const objeto = sanitizarTextoContrato(args.contrato.objeto);
  if (objeto) {
    builder.addSection('Objeto del contrato');
    builder.addQuoteBlock(objeto);
  }

  // Alcance — sistemas
  builder.addSection('Sistemas incluidos');
  if (args.sistemas.length === 0) {
    builder.addText('No se ha definido alcance de sistemas.', 9.5);
  } else {
    const rows = args.sistemas.map(s => [
      s.tipoLabel,
      s.sedeNombre || '—',
      s.sedeDireccion || '—',
    ]);
    builder.addTable(['Sistema', 'Sede', 'Dirección'], rows);
  }

  // Condiciones económicas
  builder.addSection('Condiciones económicas');
  const items = [
    { label: `Pago ${PERIODICIDAD_LABEL[args.contrato.periodicidad_pago]}`, value: eur(args.contrato.precio_periodo) },
    { label: 'Importe anual', value: eur(args.contrato.precio_total) },
    { label: 'IVA', value: args.contrato.iva_incluido ? 'Incluido' : 'No incluido' },
  ];
  builder.addTotalsBox(items, { label: 'Total anual', value: eur(args.contrato.precio_total) });

  // Cláusulas
  builder.addSection('Cláusulas');
  for (const c of args.contrato.clausulas) {
    const titulo = sanitizarTextoContrato(c.titulo) || '—';
    const contenido = sanitizarTextoContrato(c.contenido);
    builder.addBlockSubtitle(titulo);
    if (contenido) {
      // Partir por párrafos para respetar saltos de línea
      const parrafos = contenido.split('\n').filter(p => p.trim().length > 0);
      for (const p of parrafos) {
        builder.addText(p, 9.5);
      }
    }
    builder.addSpacer(2);
  }

  // Observaciones
  const obs = sanitizarTextoContrato(args.contrato.observaciones || '');
  if (obs) {
    builder.addSection('Observaciones');
    builder.addQuoteBlock(obs);
  }

  // Texto legal
  const legal = sanitizarTextoContrato(args.contrato.texto_legal || '');
  if (legal) {
    builder.addSpacer(4);
    builder.addLegalText(legal);
  }

  // Firmas
  builder.addSignatureBlock([
    { label: 'Por el PRESTADOR', imageData: args.contrato.firma_empresa || null, nombre: empresa?.razon_social || empresa?.nombre || '', esEmpresa: true },
    { label: 'Por el CLIENTE', imageData: args.contrato.firma_cliente || null, nombre: args.contrato.nombre_firmante_cliente || args.cliente.nombre_fiscal || args.cliente.nombre },
  ]);

  const pdf = builder.finalize();

  if (args.esBorrador) {
    const pageCount = pdf.getNumberOfPages();
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      try {
        // @ts-ignore — GState exists at runtime in jsPDF
        const GState = (pdf as any).GState;
        if (GState) {
          // @ts-ignore
          pdf.setGState(new GState({ opacity: 0.15 }));
        }
      } catch { /* noop */ }
      pdf.setTextColor(180, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(90);
      pdf.text('BORRADOR', pageW / 2, pageH / 2, {
        align: 'center',
        baseline: 'middle',
        angle: 35,
      });
      try {
        // @ts-ignore
        const GState = (pdf as any).GState;
        if (GState) {
          // @ts-ignore
          pdf.setGState(new GState({ opacity: 1 }));
        }
      } catch { /* noop */ }
      pdf.setTextColor(0, 0, 0);
    }
  }

  return pdf.output('blob');
}

